import React, { useState, useEffect, useRef } from "react";
import { useChat } from "./hooks/useChat";
import "./App.css";

/**
 * EngageIQ Chat Application
 *
 * Props (when used as embeddable widget):
 *   token     - JWT token for authentication
 *   eventId   - Event ID to join chat for
 *   webinarId - Webinar ID to join chat for
 *   userName  - Display name
 *   role      - User role (HOST, SPEAKER, ATTENDEE, MODERATOR)
 */
function App({ token: propToken, eventId: propEventId, webinarId: propWebinarId, userName: propUserName, email: propEmail, role: propRole }) {
  // State for join screen (when not embedded with props)
  const [joinData, setJoinData] = useState({
    username: propUserName || "",
    email: propEmail || "",
    token: propToken || "",
    eventId: propEventId || "",
    webinarId: propWebinarId || "",
    role: propRole || "ATTENDEE",
  });
  const [isJoined, setIsJoined] = useState(!!(propToken && propUserName));
  const [messageText, setMessageText] = useState("");
  const messagesEndRef = useRef(null);

  // Use the chat hook when joined
  const {
    connected,
    roomInfo,
    messages,
    typingUsers,
    participantCount,
    error,
    sendMessage,
    deleteMessage,
    emitTyping,
  } = useChat(
    isJoined
      ? {
          token: joinData.token,
          eventId: joinData.eventId || undefined,
          webinarId: joinData.webinarId || undefined,
          userName: joinData.username,
          email: joinData.email || undefined,
          role: joinData.role,
        }
      : { token: null, userName: null }
  );

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typingUsers]);

  // Handle join
  const handleJoin = (e) => {
    e.preventDefault();
    if (joinData.username.trim() && joinData.token.trim()) {
      setIsJoined(true);
    }
  };

  // Handle send
  const handleSendMessage = (e) => {
    if (e) e.preventDefault();
    if (messageText.trim()) {
      sendMessage(messageText.trim());
      setMessageText("");
    }
  };

  // Handle keyboard in textarea (Enter to send, Shift+Enter for newline)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle paste — strip HTML formatting, keep plain text
  const handlePaste = (e) => {
    e.preventDefault();
    const plainText = e.clipboardData.getData("text/plain");
    setMessageText((prev) => prev + plainText);
  };

  // Handle typing
  const handleTyping = (e) => {
    setMessageText(e.target.value);
    emitTyping();
  };

  // Format timestamp
  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Get role badge color
  const getRoleBadge = (role) => {
    const badges = {
      HOST: { label: "Host", className: "role-host" },
      SPEAKER: { label: "Speaker", className: "role-speaker" },
      MODERATOR: { label: "Mod", className: "role-moderator" },
      ATTENDEE: { label: "", className: "" },
    };
    return badges[role] || badges.ATTENDEE;
  };

  // ---------- JOIN SCREEN ----------
  if (!isJoined) {
    return (
      <div className="join-screen">
        <div className="join-card">
          <div className="join-icon">💬</div>
          <h1>EngageIQ Chat</h1>
          <p className="join-subtitle">Real-time conversations, beautifully.</p>
          <form onSubmit={handleJoin}>
            <input
              id="username-input"
              type="text"
              placeholder="Your name…"
              value={joinData.username}
              onChange={(e) =>
                setJoinData((d) => ({ ...d, username: e.target.value }))
              }
              autoFocus
              required
            />
            <input
              id="token-input"
              type="text"
              placeholder="JWT Token…"
              value={joinData.token}
              onChange={(e) =>
                setJoinData((d) => ({ ...d, token: e.target.value }))
              }
              required
            />
            <input
              id="email-input"
              type="email"
              placeholder="Email (optional)"
              value={joinData.email}
              onChange={(e) =>
                setJoinData((d) => ({ ...d, email: e.target.value }))
              }
            />
            <div className="join-row">
              <input
                id="event-id-input"
                type="text"
                placeholder="Event ID"
                value={joinData.eventId}
                onChange={(e) =>
                  setJoinData((d) => ({ ...d, eventId: e.target.value }))
                }
              />
              <span className="join-or">or</span>
              <input
                id="webinar-id-input"
                type="text"
                placeholder="Webinar ID"
                value={joinData.webinarId}
                onChange={(e) =>
                  setJoinData((d) => ({ ...d, webinarId: e.target.value }))
                }
              />
            </div>
            <select
              id="role-select"
              value={joinData.role}
              onChange={(e) =>
                setJoinData((d) => ({ ...d, role: e.target.value }))
              }
            >
              <option value="ATTENDEE">Attendee</option>
              <option value="HOST">Host</option>
              <option value="SPEAKER">Speaker</option>
              <option value="MODERATOR">Moderator</option>
            </select>
            <button id="join-button" type="submit">
              Join Chat
              <span className="btn-arrow">→</span>
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---------- CHAT SCREEN ----------
  return (
    <div className="chat-container">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="logo-icon">💬</div>
          <h2>EngageIQ</h2>
        </div>
        <div className="sidebar-user">
          <div className="avatar">
            {joinData.username.charAt(0).toUpperCase()}
          </div>
          <div className="user-info">
            <span className="user-name">{joinData.username}</span>
            <span className="user-status">
              <span
                className={`status-dot ${connected ? "" : "offline"}`}
              ></span>{" "}
              {connected ? "Online" : "Connecting..."}
            </span>
          </div>
        </div>

        {/* Connection info */}
        <div className="sidebar-section">
          <div className="sidebar-label">Room Info</div>
          {roomInfo && (
            <div className="room-info-card">
              <div className="info-row">
                <span className="info-label">Type</span>
                <span className="info-value">{roomInfo.roomType}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Online</span>
                <span className="info-value">{participantCount}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Status</span>
                <span className={`info-value ${roomInfo.isActive ? "active" : "inactive"}`}>
                  {roomInfo.isActive ? "Active" : "Disabled"}
                </span>
              </div>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          <div className="nav-item active">
            <span className="nav-icon">🌐</span> Chat Room
          </div>
        </nav>
      </aside>

      {/* Main Chat Area */}
      <main className="chat-main">
        <header className="chat-header">
          <div>
            <h3>
              # {roomInfo?.roomType === "WEBINAR" ? "Webinar" : "Event"} Chat
            </h3>
            <span className="header-subtitle">
              {participantCount} online · {connected ? "Connected" : "Reconnecting..."}
            </span>
          </div>
        </header>

        {/* Error banner */}
        {error && (
          <div className="error-banner">
            ⚠️ {error}
          </div>
        )}

        <div className="messages-area" id="messages-area">
          {messages.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon">🚀</span>
              <p>No messages yet. Start the conversation!</p>
            </div>
          )}
          {messages.map((msg) => {
            if (msg.isSystem || msg.messageType === "SYSTEM") {
              return (
                <div key={msg.id} className="system-message">
                  {msg.message}
                </div>
              );
            }

            if (msg.isDeleted) {
              return (
                <div key={msg.id} className="system-message deleted-msg">
                  🗑️ Message deleted
                </div>
              );
            }

            const isOwn = msg.senderName === joinData.username;
            const roleBadge = getRoleBadge(msg.senderRole);

            return (
              <div
                key={msg.id}
                className={`message-row ${isOwn ? "own" : "other"}`}
              >
                {!isOwn && (
                  <div className="msg-avatar">
                    {msg.senderName?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="message-bubble">
                  {!isOwn && (
                    <span className="msg-sender">
                      {msg.senderName}
                      {roleBadge.label && (
                        <span className={`role-badge ${roleBadge.className}`}>
                          {roleBadge.label}
                        </span>
                      )}
                    </span>
                  )}
                  {msg.messageType === "ANNOUNCEMENT" && (
                    <span className="announcement-badge">📢 Announcement</span>
                  )}
                  <p className="msg-text" style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                  <span className="msg-time">{formatTime(msg.createdAt)}</span>
                </div>
                {/* Delete button for own messages or if HOST/MODERATOR */}
                {(isOwn ||
                  joinData.role === "HOST" ||
                  joinData.role === "MODERATOR") && (
                  <button
                    className="msg-delete-btn"
                    onClick={() => deleteMessage(msg.id)}
                    title="Delete message"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}

          {typingUsers.length > 0 && (
            <div className="typing-indicator">
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              {typingUsers.join(", ")}{" "}
              {typingUsers.length === 1 ? "is" : "are"} typing…
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="message-form" onSubmit={handleSendMessage}>
          <textarea
            id="message-input"
            placeholder="Type a message… (Shift+Enter for new line)"
            value={messageText}
            onChange={handleTyping}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            maxLength={2000}
            rows={1}
            autoFocus
          />
          <button id="send-button" type="submit" disabled={!connected}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </form>
      </main>
    </div>
  );
}

export default App;
